import joi from 'joi';
import React, { useEffect, useMemo } from 'react';
import {
  $setCurrencyBody,
  ExpirationType,
  SetCurrencyBody,
} from '@loyalty/shared-common/gamification-api-client';
import { Button, Tooltip } from '@mui/material';
import { Feature, defaultCurrencyImage, getLocalizedProperty } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormProvider, useForm } from 'react-hook-form';
import { QlfPlaceholder } from '@qualifio/ui-commons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { useLeaverPrompt } from '@qualifio/ui-commons/dist/components/qlf-prompt';
import { useTranslation } from 'react-i18next';
import { CurrencyEdition } from '../panels';
import { DetailForm } from '../../../shared/layout/DetailForm';
import { QlfPlaceholderIcon } from '../../../shared/qlf-placeholder-icon/QlfPlaceholderIcon';
import { currenciesIcon } from '../../../shared/icons';
import { useCurrencies } from '../../CurrenciesContext';
import { useFeatures } from '../../../features/FeaturesContext';
import { useJoiResolver } from '../../../shared/forms/use-joi-resolver';
import { useProgram } from '../../../loyalty/ProgramContext';

export function CurrencyDetails(): React.ReactElement {
  const { currencies, selectedCurrency, setCurrency, createCurrency, isLoading } = useCurrencies();
  const { t } = useTranslation();
  const { program } = useProgram();
  const { features } = useFeatures();
  const { write: currencyWrite } = features[Feature.CURRENCY];

  const alreadyExistingCurrencyI18n = useMemo(
    () =>
      currencies.filter(curr => curr.id !== selectedCurrency?.id).map(cur => cur.i18nAttributes),
    [currencies, selectedCurrency],
  );

  const currencyFormSchema = useMemo(
    () =>
      $setCurrencyBody.keys({
        i18nAttributes: joi.object().pattern(
          /./,
          joi.object({
            name: joi.string().custom((value: string, helper) => {
              let alreadyUsed = false;
              program.languages.forEach(language => {
                alreadyExistingCurrencyI18n.forEach(currencyI18n => {
                  if (currencyI18n[language]?.name === value) {
                    alreadyUsed = true;
                  }
                });
              });

              if (alreadyUsed) {
                return helper.error('custom.currency.name.alreadyUsed');
              }
              return value;
            }),
            description: joi.string().required(),
            image: joi.string().required(),
          }),
        ),
      }),
    [alreadyExistingCurrencyI18n],
  );

  const resolver = useJoiResolver(currencyFormSchema);
  const form = useForm<SetCurrencyBody>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: selectedCurrency || {
      i18nAttributes: Object.fromEntries(
        program.languages.map(lang => [
          lang,
          {
            name: '',
            description: '',
            image: defaultCurrencyImage,
          },
        ]),
      ),
      expiration: {
        type: ExpirationType.NONE,
      },
      retroActive: false,
    },
    resolver,
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting, errors },
  } = form;

  const onSubmit = async (value: SetCurrencyBody) => {
    for (const language in value.i18nAttributes) {
      const currenciesNames = currencies
        .filter(cur => cur.id !== value.id)
        .map(cur => getLocalizedProperty(cur, 'name', language));
      if (currenciesNames.includes(getLocalizedProperty(value, 'name', language))) {
        throw new Error('Currency name already used !');
      }
    }

    setCurrency(value);
  };

  useEffect(() => {
    reset(selectedCurrency);
    // eslint-disable-next-line
  }, [selectedCurrency]);

  useLeaverPrompt(isDirty || isSubmitting);

  if (currencies.length === 0 || !selectedCurrency) {
    return (
      <QlfPlaceholder
        icon={<QlfPlaceholderIcon icon={currenciesIcon} />}
        primaryText={t('currencies.list.empty.title')}
        secondaryText={t('currencies.list.empty.description')}
        actions={
          <Tooltip
            title={currencyWrite ? '' : t('shared.action.permission.required')}
            placement="left"
          >
            <div>
              <Button
                color="primary"
                disabled={!currencyWrite}
                startIcon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => createCurrency()}
                style={{ marginTop: '20px' }}
                id={'empty-list-create-currency'}
              >
                {t('currencies.list.empty.create-action')}
              </Button>
            </div>
          </Tooltip>
        }
      />
    );
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
        <DetailForm key={selectedCurrency?.id}>
          <DetailForm.Form>
            <CurrencyEdition />
          </DetailForm.Form>
          <DetailForm.Footer>
            <Tooltip
              title={
                !currencyWrite
                  ? t('shared.action.permission.required')
                  : selectedCurrency.deletedAt
                  ? t('currencies.archived.edit.forbidden')
                  : ''
              }
              placement="left"
            >
              <div>
                <Button
                  startIcon={
                    <FontAwesomeIcon
                      fontSize="inherit"
                      icon={isSubmitting || isLoading('setCurrency') ? faSpinner : faCheck}
                      spin={isSubmitting || isLoading('setCurrency')}
                    />
                  }
                  color="success"
                  onClick={handleSubmit(onSubmit)}
                  disabled={
                    !isDirty ||
                    isSubmitting ||
                    isLoading('setCurrency') ||
                    !currencyWrite ||
                    !!selectedCurrency.deletedAt ||
                    !!errors?.i18nAttributes
                  }
                  id="currency-save-button-id"
                >
                  {t('currencies.save.button')}
                </Button>
              </div>
            </Tooltip>
          </DetailForm.Footer>
        </DetailForm>
      </form>
    </FormProvider>
  );
}
